import React, { useEffect, useState, useCallback } from "react";
import Navbar from "../components/Navbar";
import MemberCard from "../components/MemberCard";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { NbButton, NbInput, NbSelect } from "../components/nb";
import { SlidersHorizontal, Crown, Lock } from "lucide-react";

const INTERESTS = ["Travel", "Music", "Foodie", "Fitness", "Art", "Gaming", "Reading", "Movies", "Coffee", "Hiking", "Photography", "Dancing", "Yoga", "Cooking", "Pets", "Wine"];

export default function Browse() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ gender: "", min_age: "", max_age: "", interest: "", location: "", online_now: false, recently_active: false });
  const isPremium = user.membership === "premium";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== false) params[k] = v; });
      const { data } = await api.get("/members", { params });
      setMembers(data);
    } catch { toast.error("Failed to load members."); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, []);

  const like = async (m) => {
    try {
      const { data } = await api.post(`/like/${m.id}`);
      setMembers((prev) => prev.map((x) => x.id === m.id ? { ...x, liked: data.liked } : x));
      if (data.matched) toast.success(`It's a match with ${m.name}! 🎉`);
    } catch {}
  };
  const fav = async (m) => {
    try {
      const { data } = await api.post(`/favorite/${m.id}`);
      setMembers((prev) => prev.map((x) => x.id === m.id ? { ...x, favorited: data.favorited } : x));
    } catch {}
  };

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-head font-black text-4xl tracking-tighter mb-1">Browse Members</h1>
        <p className="text-gray-600 font-medium mb-6">Discover singles near you.</p>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <aside className="lg:col-span-1">
            <div className="bg-white nb-border nb-shadow p-5 lg:sticky lg:top-20">
              <div className="flex items-center gap-2 mb-4 font-head font-black text-lg"><SlidersHorizontal className="w-5 h-5" /> Filters</div>
              <div className="space-y-3">
                <div><label className="font-head font-bold text-xs uppercase">Gender</label>
                  <NbSelect data-testid="filter-gender" value={filters.gender} onChange={(e) => setFilters({ ...filters, gender: e.target.value })} className="mt-1">
                    <option value="">Any</option><option>Woman</option><option>Man</option><option>Non-binary</option><option>Other</option>
                  </NbSelect></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><label className="font-head font-bold text-xs uppercase">Min Age</label><NbInput data-testid="filter-min-age" type="number" value={filters.min_age} onChange={(e) => setFilters({ ...filters, min_age: e.target.value })} className="mt-1" /></div>
                  <div><label className="font-head font-bold text-xs uppercase">Max Age</label><NbInput data-testid="filter-max-age" type="number" value={filters.max_age} onChange={(e) => setFilters({ ...filters, max_age: e.target.value })} className="mt-1" /></div>
                </div>
                <div><label className="font-head font-bold text-xs uppercase">Location</label><NbInput data-testid="filter-location" value={filters.location} onChange={(e) => setFilters({ ...filters, location: e.target.value })} className="mt-1" placeholder="City" /></div>
                <div><label className="font-head font-bold text-xs uppercase">Interest</label>
                  <NbSelect data-testid="filter-interest" value={filters.interest} onChange={(e) => setFilters({ ...filters, interest: e.target.value })} className="mt-1">
                    <option value="">Any</option>{INTERESTS.map((i) => <option key={i}>{i}</option>)}
                  </NbSelect></div>
                <label className="flex items-center gap-2 font-medium text-sm cursor-pointer">
                  <input type="checkbox" data-testid="filter-online" checked={filters.online_now} onChange={(e) => setFilters({ ...filters, online_now: e.target.checked })} className="w-5 h-5 nb-border accent-[#8B1E3F]" /> Online now
                </label>
                <div className={`${isPremium ? "" : "opacity-60"} bg-[#F3F3F1] nb-border p-2`}>
                  <div className="flex items-center gap-1 text-xs font-head font-bold uppercase mb-1"><Crown className="w-3.5 h-3.5" /> Premium filters {!isPremium && <Lock className="w-3 h-3" />}</div>
                  <label className="flex items-center gap-2 font-medium text-sm cursor-pointer">
                    <input type="checkbox" data-testid="filter-recent" disabled={!isPremium} checked={filters.recently_active} onChange={(e) => setFilters({ ...filters, recently_active: e.target.checked })} className="w-5 h-5 nb-border accent-[#8B1E3F]" /> Recently active
                  </label>
                </div>
                <NbButton onClick={load} data-testid="apply-filters" className="w-full">Apply Filters</NbButton>
              </div>
            </div>
          </aside>

          <main className="lg:col-span-3">
            {loading ? (
              <div className="text-center py-20 font-head font-bold uppercase">Loading members...</div>
            ) : members.length === 0 ? (
              <div className="bg-white nb-border nb-shadow p-10 text-center font-medium">No members match your filters yet.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {members.map((m) => <MemberCard key={m.id} m={m} onLike={like} onFav={fav} />)}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
